import jsPDF from 'jspdf';
import { InstructionStep } from '../../interfaces';
import { calculateAspectRatioFit, canvasToImage, capitalize, generateCanvas, loadImage } from './utils';

import { formatDistance } from '../../utils/distance';

interface PrintOptions {
	mapCanvas: any;
	steps: Array<InstructionStep>;
	distance: string;
	duration: {
		time: string;
		label: string;
	};
	logo: string;
	origin: string;
	destination: string;
}

function loadIcon(type: string, modifier: string) {
	return new Promise((resolve, reject) => {
		const src = `${process.env.PUBLIC_URL}${'/directions-icons/'}${(type + (modifier ? '_' + modifier : ''))
			.split(' ')
			.join('_')}.svg`;
		loadImage(src).then(resolve).catch(reject);
	});
}

function generatePDF(canvasArr: any[]) {
	const doc = new jsPDF('p', 'mm', 'a4');

	canvasArr.forEach((canvas, index) => {
		const imgData = canvas.toDataURL('image/png', 1.0);

		doc.addImage(imgData, 'PNG', 10, 10);

		if (canvasArr.length - 1 > index) {
			doc.addPage();
		}
	});

	// doc.autoPrint();

	return doc.output('bloburl') as string;
}

function addLogo(ctx: CanvasRenderingContext2D, path: string) {
	loadImage(path).then(img => {
		const dimensions = calculateAspectRatioFit(img.width, img.height, 50, 50);
		ctx.drawImage(img, 10, 0, dimensions.width, dimensions.height);
	});
}

async function addText(
	ctx: CanvasRenderingContext2D,
	text: string,
	color = 'black',
	fontSize = '14px',
	x: number,
	y: number
) {
	ctx.fillStyle = color;
	ctx.font = fontSize + ' Helvetica';
	ctx.fillText(text, x, y);
}

export function generate({
	mapCanvas,
	steps,
	distance,
	duration,
	logo,
	origin,
	destination
}: PrintOptions): Promise<string> {
	const instructions = steps.map(instruction => ({
		distance: formatDistance(instruction.distance, 2, 1000),
		instruction: instruction.maneuver.instruction,
		modifier: instruction.maneuver.modifier,
		type: instruction.maneuver.type
	}));

	return new Promise(async resolve => {
		let { canvas, ctx } = generateCanvas();
		let currentTop = 0;
		const marginTop = 60;
		const textMarginLeft = 50;
		const distanceMarginTop = 25;
		const canvasArr = [];

		if (!ctx) return;

		// Map
		const mapImage = await canvasToImage(mapCanvas);
		const mapDimensions = calculateAspectRatioFit(mapImage.width, mapImage.height, 1000, 480);

		ctx.drawImage(mapImage, 5, currentTop + 60, mapDimensions.width, mapDimensions.height);
		currentTop = mapDimensions.height + 100;
		//

		// Origin and destination
		addText(ctx, `${origin} to ${destination}`, 'black', '14px', 70, 16);

		// Distance and destination
		addText(
			ctx,
			`${capitalize(steps[0].mode)} ${distance} miles, ${duration.time} ${duration.label}`,
			'black',
			'14px',
			70,
			36
		);

		addLogo(ctx, logo);

		// Instructions
		let index = 11;

		for (const instructionKey in instructions) {
			const { instruction, type, modifier, distance } = instructions[instructionKey];

			if (!ctx) {
				return;
			}

			try {
				// @ts-ignore
				const icon: HTMLImageElement = await loadIcon(type, modifier!);

				if (ctx && icon && icon.src) {
					ctx.drawImage(icon, 10, currentTop - 5);
				}
			} catch (ex: any) {
				console.log('Cannot load icon', ex.message);
			}

			addText(ctx, instruction, 'black', '14px', textMarginLeft, currentTop);
			addText(ctx, `${distance} miles`, '#9b9b9b', '12px', textMarginLeft, currentTop + distanceMarginTop);

			currentTop += marginTop;

			if (index >= 18) {
				const newCanvas = generateCanvas();

				canvasArr.push(canvas);

				canvas = newCanvas.canvas;
				ctx = newCanvas.ctx;

				currentTop = 10;
				index = 0;
			}
			index += 1;
		}

		if (index > 1) canvasArr.push(canvas);

		const blob = generatePDF(canvasArr);

		resolve(blob);
	});
}
